// The weekly challenge (scenario Q9, PLAN-2026-11-01-the-weekly-challenge §7).
//
// ONE WEEK, THE SAME FOR EVERYBODY ON THE SITE, reached from a league so Back
// is its League page. The banner opens it in the week's two positions'
// colors (B-619): the week and its state, the title with the rules behind an
// info button, the positions it plays, what to do and until when, a pip a
// pick and my result once in. Then how the week scores, each category's
// points in a badge; my entry — a pick a tier, edged in its position's color
// with its tier's medal, a captain, its points and place once they are in;
// then each drawn position's tiers, a table each, every player with his game
// and the numbers a person weighs: his average in each of the week's
// categories over the games his tier was measured on, and, once his game is
// read, what he did. Never a combined projection or a suggestion: the weighing
// is the player's (DIRECTION U1). Then the boards: the league's members, and
// everybody's top by their challenge names, the first three on medals.
//
// TWO WEEKS CAN BE LIVE AT ONCE (B-620): the next week opens at this one's
// first kickoff, so the page lands on the week taking picks, and a strip under
// the banner says where I stand in the week still being played and goes to
// it; on that week, the strip goes to the one taking picks.
//
// THE NAME COMES FIRST. The first pick asks for the name the public board
// shows, saying so as it asks (CLAUDE.md, say what will happen), and the pick
// is saved once the name is.
//
// A REAL LEAGUE'S ALONE: the practice league's simulator has no challenge, and
// a visitor to the demo league has no account to enter with; each says so.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  valueOf,
  type ChallengeBoardViewShape,
  type ChallengePlayerShape,
  type ChallengeWeekViewShape,
  type Snapshot,
} from '@fsp/domain';
import {
  saveChallengeEntry,
  saveChallengeName,
  useChallengeBoard,
  useChallengeWeek,
} from '../data/challenge';
import { useActiveLeague } from '../data/LeaguesProvider';
import { useAtLeast } from '../data/detail';
import { deviceZone, useReaderZone, useRouteLeagueId } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { useSession } from '../data/session';
import { PositionPill, StatusBadge, TeamMark } from '../kit';
import { Icon } from '../kit/Icon';
import { Panel } from '../kit/Panel';
import { TableScroll } from '../kit/TableScroll';
import {
  Button,
  Caption,
  Chip,
  Colored,
  Dialog,
  Line,
  Stack,
  Text,
  TextField,
  Title,
  tell,
} from '../kit/ui';
import { useTier } from '../shell/useTier';
import { proTeamOf } from '../sports/registry';
import { ChallengeBanner, ChallengePips, positionColor } from './shared/ChallengeBanner';
import { ChallengeInfo, ChallengeSteps } from './shared/ChallengeHow';
import { FromLeague } from './shared/FromLeague';
import { fmtWhen } from './shared/RosterMoveSheet';
import { WeekStepper } from './shared/WeekStepper';
import '../styles/screens/challenge.css';

/** A total to a tenth, as the challenge counts. */
export function tenths(n: number): string {
  return n.toFixed(1);
}

/** "1st", "2nd", "23rd". */
export function ordinal(n: number): string {
  const s = n % 100;
  if (s >= 11 && s <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
}

/** A position's players, said as a group: "Quarterbacks". */
export function plural(name: string): string {
  return `${name}s`;
}

/** One slot of an entry: a position's tier. */
interface Slot {
  position: ChallengeWeekViewShape['positions'][number];
  tier: number;
  players: ChallengePlayerShape[];
}

export function slotsOf(view: ChallengeWeekViewShape): Slot[] {
  return view.positions.flatMap((position) =>
    position.tiers.map((players, tier) => ({ position, tier, players })),
  );
}

export function ChallengeScreen() {
  const [params, setParams] = useSearchParams();
  const num = (raw: string | null) => (raw !== null && /^\d+$/.test(raw) ? Number(raw) : undefined);
  const season = num(params.get('season'));
  const period = num(params.get('period'));
  const source = useLeagueSource();
  const card = useActiveLeague();
  const leagueId = useRouteLeagueId();
  const { session } = useSession();
  const sport = source === 'api' && session !== null ? (card?.league.sport ?? null) : null;
  const week = useChallengeWeek(sport, season, period);
  // A SAVE'S ANSWER IS THE WEEK AS IT NOW STANDS, drawn at once, until the
  // read answers again (as pick'em does).
  const [saved, setSaved] = useState<ChallengeWeekViewShape | null>(null);
  const fresh = valueOf(week.snapshot);
  useEffect(() => setSaved(null), [fresh]);

  if (source === 'sim' || session === null) {
    return (
      <div className="fsp-screen">
        <div className="fsp-page-scroll">
          <div className="fsp-challenge">
            <Panel title="Weekly challenge">
              <Stack gap={3}>
                {/* TODO(Q9): the weekly challenge in the practice league's simulator. */}
                <Text tone="secondary" block>
                  {source === 'sim'
                    ? 'The weekly challenge plays the real NFL week, the same for everybody on the site, so it is in your real leagues and not in the practice league.'
                    : 'The weekly challenge is the same for everybody on the site. Sign in to make your picks.'}
                </Text>
                <ChallengeSteps captain={1.5} />
              </Stack>
            </Panel>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="fsp-screen">
      <FromLeague
        snapshot={week.snapshot as Snapshot<ChallengeWeekViewShape>}
        onRetry={week.reload}
      >
        {(fetched) => {
          const view =
            saved !== null && saved.season === fetched.season && saved.period === fetched.period
              ? saved
              : fetched;
          return (
            <ChallengeBody
              view={view}
              leagueId={leagueId}
              onWeek={(p) => {
                setSaved(null);
                setParams({ season: String(view.season), period: String(p) }, { replace: true });
              }}
              onSave={async (picks, captain) => {
                const out = await saveChallengeEntry(
                  view.sport,
                  view.season,
                  view.period,
                  picks,
                  captain,
                );
                if (out.ok) {
                  setSaved(out.view);
                  week.reload();
                } else tell(out.message, 'bad');
                return out.ok;
              }}
              onNamed={(name) => setSaved({ ...view, name })}
            />
          );
        }}
      </FromLeague>
    </div>
  );
}

function ChallengeBody({
  view,
  leagueId,
  onWeek,
  onSave,
  onNamed,
}: {
  view: ChallengeWeekViewShape;
  leagueId: string | null;
  onWeek: (period: number) => void;
  /** Resolves true once saved. */
  onSave: (picks: (string | null)[], captain: string | null) => Promise<boolean>;
  onNamed: (name: string) => void;
}) {
  const zone = useReaderZone() ?? deviceZone();
  // Simple keeps the result and what a pick is weighed on, and drops the
  // tiers' points a game and each category's share of a score (B-609).
  const numbers = useAtLeast('standard');
  const tier = useTier();
  const wide = tier === 'desktop' || tier === 'large';
  const slots = slotsOf(view);
  const picks = view.mine?.picks ?? slots.map(() => null);
  const captain = view.mine?.captain ?? null;
  const [busy, setBusy] = useState(false);
  // A PICK WAITING ON A NAME: the name sheet saves the name, then the pick.
  const [waiting, setWaiting] = useState<{
    picks: (string | null)[];
    captain: string | null;
  } | null>(null);
  // THE NAME CHANGED, from Your entry: the same sheet, with no pick after it.
  const [renaming, setRenaming] = useState(false);
  const open = view.state === 'open';
  const board = useChallengeBoard(view.sport, null, view.season, view.period, view.entries);
  const league = useChallengeBoard(view.sport, leagueId, view.season, view.period, view.entries);
  // THE OTHER WEEK LIVE: the one taking picks when this is not it, or, on the
  // one taking picks, the week before it still being played.
  const taking = [...view.weeks].reverse().find((w) => w.state === 'open') ?? null;
  const other =
    taking !== null && taking.period !== view.period
      ? taking
      : (view.weeks.find(
          (w) => w.period !== view.period && (w.state === 'open' || w.state === 'playing'),
        ) ?? null);
  const otherRead = useChallengeWeek(
    other === null ? null : view.sport,
    other?.season,
    other?.period,
  );

  const save = async (next: (string | null)[], nextCaptain: string | null) => {
    if (busy) return;
    if (view.name === null) {
      setWaiting({ picks: next, captain: nextCaptain });
      return;
    }
    setBusy(true);
    await onSave(next, nextCaptain);
    setBusy(false);
  };
  const pick = (slot: number, id: string) => {
    const next = picks.map((p, i) => (i === slot ? (p === id ? null : id) : p));
    const was = picks[slot];
    void save(next, captain !== null && captain === was && was !== next[slot] ? null : captain);
  };

  const banner = (
    <WeekBanner
      view={view}
      zone={zone}
      slots={slots.map((s) => s.position.position)}
      picks={picks}
      captain={captain}
    />
  );
  const scoring = view.positions.length > 0 ? <Scoring view={view} /> : null;
  const entry = (
    <EntryPanel
      view={view}
      slots={slots}
      picks={picks}
      captain={captain}
      numbers={numbers}
      busy={busy}
      onCaptain={(id) => void save(picks, id)}
      onRename={() => setRenaming(true)}
    />
  );
  const positions = view.positions.map((p) => (
    <PositionPanel
      key={p.position}
      position={p}
      phone={tier === 'phone'}
      firstSlot={slots.findIndex((s) => s.position.position === p.position)}
      picks={picks}
      captain={captain}
      zone={zone}
      numbers={numbers}
      canPick={open && !busy}
      onPick={pick}
    />
  ));
  const boards = <Boards board={valueOf(board)} league={valueOf(league)} numbers={numbers} />;
  const how = (
    <Panel title="How the weekly challenge works" bodyClass="fsp-challenge-body">
      <ChallengeSteps captain={view.captain} />
    </Panel>
  );
  return (
    <div className="fsp-page-scroll">
      <div className="fsp-challenge" data-wide={wide || undefined}>
        <Stack gap={4}>
          {banner}
          {other !== null ? (
            <OtherWeek
              other={other}
              taking={taking !== null && other.period === taking.period}
              read={valueOf(otherRead.snapshot)}
              onGo={onWeek}
            />
          ) : null}
          {view.weeks.length > 1 ? (
            <WeekStepper
              periods={view.weeks.map((w) => w.period)}
              current={view.period}
              label={view.label}
              onGo={onWeek}
            />
          ) : null}
          {wide ? (
            <div className="fsp-challenge-grid">
              <Stack gap={4}>
                {scoring}
                {positions}
              </Stack>
              <Stack gap={4}>
                {view.positions.length > 0 ? entry : null}
                {boards}
                {how}
              </Stack>
            </div>
          ) : (
            <Stack gap={4}>
              {scoring}
              {view.positions.length > 0 ? entry : null}
              {positions}
              {boards}
              {how}
            </Stack>
          )}
        </Stack>
        <NameSheet
          opened={waiting !== null || renaming}
          picking={waiting !== null}
          current={view.name}
          onClose={() => {
            setWaiting(null);
            setRenaming(false);
          }}
          onSaved={async (name) => {
            onNamed(name);
            const w = waiting;
            setWaiting(null);
            setRenaming(false);
            if (w !== null) {
              setBusy(true);
              await onSave(w.picks, w.captain);
              setBusy(false);
            }
          }}
        />
      </div>
    </div>
  );
}

/**
 * WHERE THE WEEK STANDS, on the week's banner in its two positions' colors
 * (B-619): the week and its state, the title, the positions it plays, what
 * to do and until when, a pip a pick, and my result once it is in.
 */
function WeekBanner({
  view,
  zone,
  slots,
  picks,
  captain,
}: {
  view: ChallengeWeekViewShape;
  zone: string;
  /** Each slot's position, in slot order. */
  slots: string[];
  picks: (string | null)[];
  captain: string | null;
}) {
  const picked = picks.filter((p) => p !== null).length;
  // SOME OF THE WEEK'S GAMES HAVE STARTED: said, so a page of locked rows is
  // not a surprise (B-620).
  const started = view.firstKickoff !== null && Date.now() >= Date.parse(view.firstKickoff);
  const sentence =
    view.state === 'upcoming'
      ? `${view.label}’s challenge opens ${fmtWhen(view.opensAt, zone, true)}.`
      : view.state === 'open'
        ? started
          ? 'Some of this week’s games have started. A player whose game hasn’t can still be picked or changed.'
          : picked === 0
            ? 'Pick one player from each tier and make one of them your captain. Each pick locks when his game starts.'
            : 'A pick can change until his game starts.'
        : view.state === 'playing'
          ? 'Every game this week has started. Points come in the morning after each game.'
          : `${view.label} is over.`;
  // THE WEEK'S FIRST GAME, while none has started: a week opens days ahead
  // (a season's first, a week ahead, B-621), and this is when the first
  // pick locks.
  const firstGame = view.state === 'open' && !started ? view.firstKickoff : null;
  const mine = view.mine;
  const result =
    mine !== null && mine.place !== null
      ? `${view.state === 'over' ? 'You scored' : 'You have'} ${tenths(mine.points)}: ${ordinal(mine.place)} of ${view.entries}${view.state === 'over' ? '' : ' so far'}.`
      : null;
  return (
    <ChallengeBanner positions={view.positions.map((p) => p.position)}>
      <Stack gap={2}>
        <Line gap={2}>
          <span className="fsp-challenge-eyebrow">{view.label}</span>
          {view.state === 'open' ? (
            <Chip tone="good">Open</Chip>
          ) : view.state === 'upcoming' ? (
            <Chip icon="when">Opens soon</Chip>
          ) : view.state === 'playing' ? (
            <Chip icon="locked">Locked</Chip>
          ) : (
            <Chip icon="trophy">Final</Chip>
          )}
        </Line>
        <Stack gap={1}>
          <Line gap={1}>
            <Title>Weekly challenge</Title>
            <ChallengeInfo captain={view.captain} />
          </Line>
          <Text size="sm" tone="secondary" block>
            Everybody on the site plays the same week.
          </Text>
        </Stack>
        {view.positions.length > 0 ? (
          <ul className="fsp-challenge-banner-positions" aria-label="This week’s positions">
            {view.positions.map((p) => (
              <li key={p.position}>
                <PositionPill pos={p.position} />
                <span>{plural(p.name)}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <Text block>{sentence}</Text>
        {firstGame !== null ? (
          <Text size="sm" tone="secondary" block>
            The week’s first game is {fmtWhen(firstGame, zone, true)}.
          </Text>
        ) : null}
        {view.positions.length > 0 && (view.state === 'open' || mine !== null) ? (
          <ChallengePips slots={slots} picks={picks} captain={captain} />
        ) : null}
        {result !== null ? (
          <Text weight="strong" block>
            {result}
          </Text>
        ) : null}
      </Stack>
    </ChallengeBanner>
  );
}

/**
 * THE OTHER WEEK LIVE BESIDE THIS ONE (B-620), a strip under the banner: the
 * week taking picks, with how far my entry has got; or the week before it,
 * with where I stand in it and, while it is under way, that a player whose
 * game has not started can still go in; and the way to it.
 */
function OtherWeek({
  other,
  taking,
  read,
  onGo,
}: {
  other: ChallengeWeekViewShape['weeks'][number];
  /** It is the latest week taking picks, and this is not. */
  taking: boolean;
  /** Its own view once read, for my entry in it. */
  read: ChallengeWeekViewShape | null;
  onGo: (period: number) => void;
}) {
  const mine = read?.mine ?? null;
  const slots = read === null ? 0 : slotsOf(read).length;
  const picked = mine?.picks.filter((p) => p !== null).length ?? 0;
  const plays = read?.positions.map((p) => plural(p.name).toLowerCase()).join(' and ') ?? '';
  // A WEEK STILL UNDER WAY takes a player whose game has not started.
  const underWay = !taking && other.state === 'open';
  const lines: string[] = [];
  if (read !== null && taking)
    lines.push(
      mine === null || picked === 0
        ? 'You haven’t entered yet.'
        : picked === slots
          ? 'Your entry is in.'
          : `You’ve picked ${picked} of ${slots}.`,
    );
  if (read !== null && !taking) {
    if (mine !== null && mine.place !== null)
      lines.push(
        `You have ${tenths(mine.points)}: ${ordinal(mine.place)} of ${read.entries} so far.`,
      );
    if (underWay && picked < slots)
      lines.push(
        picked === 0
          ? 'A player whose game hasn’t started can still be picked.'
          : `You’ve picked ${picked} of ${slots}. A player whose game hasn’t started can still go in.`,
      );
    else if (mine !== null && mine.place === null)
      lines.push(
        picked === slots
          ? 'Your entry is in. Points come in the morning after each game.'
          : 'Points come in the morning after each game.',
      );
  }
  return (
    <div className="fsp-challenge-other" data-taking={taking ? 'true' : undefined}>
      <Stack gap={1}>
        <Text weight="strong" block>
          {taking
            ? `${other.label}’s challenge is open${plays === '' ? '' : `: ${plays}`}.`
            : underWay
              ? `${other.label} is under way.`
              : `${other.label} is being played.`}
        </Text>
        {lines.map((l) => (
          <Text key={l} size="sm" tone="secondary" block>
            {l}
          </Text>
        ))}
      </Stack>
      <Button
        size="xs"
        variant={taking ? 'primary' : 'secondary'}
        iconRight="next"
        onClick={() => onGo(other.period)}
      >
        {taking ? (picked === 0 ? 'Make your picks' : 'Change your picks') : `See ${other.label}`}
      </Button>
    </div>
  );
}

/** A category's points as its badge reads them: "+6", "−2". */
function signed(points: number): string {
  return points < 0 ? `−${-points}` : `+${points}`;
}

/**
 * HOW THE WEEK SCORES, read before its players: each drawn position in its
 * own color, each of its categories with its points in a badge and how it
 * scores in words, and the captain's share.
 */
function Scoring({ view }: { view: ChallengeWeekViewShape }) {
  return (
    <Panel title={`How ${view.label} scores`} bodyClass="fsp-challenge-body">
      <Stack gap={3}>
        <div className="fsp-challenge-scoring">
          {view.positions.map((p) => (
            <Colored
              key={p.position}
              className="fsp-challenge-scoring-pos"
              colors={{ '--fsp-challenge-pos': positionColor(p.position) }}
            >
              <div className="fsp-challenge-scoring-head">
                <PositionPill pos={p.position} size="sm" />
                <Text weight="strong">{plural(p.name)} score</Text>
              </div>
              <ul className="fsp-challenge-cats">
                {p.categories.map((c) => (
                  <li key={c.id}>
                    {/* THE WORDS SAY IT: the badge is the same number, to be seen at a glance. */}
                    <span
                      className="fsp-challenge-score"
                      data-against={c.against ? 'true' : undefined}
                      aria-hidden
                    >
                      {signed(c.points)}
                    </span>
                    <span className="fsp-challenge-cat">
                      <Text weight="strong" block>
                        {c.name}
                      </Text>
                      <Text size="sm" tone="secondary" block>
                        {c.rate}
                        {c.needs !== null ? `, with ${c.needs}` : ''}
                      </Text>
                    </span>
                  </li>
                ))}
              </ul>
            </Colored>
          ))}
        </div>
        <div className="fsp-challenge-captain-line">
          <Icon name="crown" size="sm" />
          <Text size="sm" tone="secondary">
            Your captain’s points count{' '}
            {view.captain === 1.5 ? 'one and a half times' : `${view.captain} times`}.
          </Text>
        </div>
      </Stack>
    </Panel>
  );
}

/**
 * A tier's medal, its number on gold, silver or bronze for the first three,
 * as the tiers go by points a game; a board's first three places wear them
 * too. `hidden` where the words beside it already say the number.
 */
export function Medal({ n, hidden = false }: { n: number; hidden?: boolean }) {
  return (
    <span
      className="fsp-challenge-medal"
      data-medal={n <= 3 ? String(n) : undefined}
      aria-hidden={hidden || undefined}
    >
      {n}
    </span>
  );
}

/** A player's club in its own color, a dot before its code. */
function ClubDot({ code }: { code: string | null }) {
  const club = code === null ? undefined : proTeamOf(code);
  if (club === undefined) return null;
  return (
    <Colored
      inline
      className="fsp-challenge-club"
      colors={{ '--fsp-challenge-club': club.palette.primary }}
    />
  );
}

/** My entry: a line a slot, its player or a gap, the captain marked, and its points once in. */
function EntryPanel({
  view,
  slots,
  picks,
  captain,
  numbers,
  busy,
  onCaptain,
  onRename,
}: {
  view: ChallengeWeekViewShape;
  slots: Slot[];
  picks: (string | null)[];
  captain: string | null;
  numbers: boolean;
  busy: boolean;
  onCaptain: (id: string) => void;
  /** Change the name everybody's board shows. */
  onRename: () => void;
}) {
  const open = view.state === 'open';
  return (
    <Panel title="Your entry" bodyClass="fsp-challenge-body">
      <ol className="fsp-challenge-entry">
        {slots.map((s, i) => {
          const id = picks[i] ?? null;
          const p = id === null ? null : (s.players.find((x) => x.player.id === id) ?? null);
          const isCaptain = id !== null && id === captain;
          return (
            <li key={`${s.position.position}-${s.tier}`}>
              <Colored
                className="fsp-challenge-entry-row"
                data-captain={isCaptain ? 'true' : undefined}
                data-empty={id === null ? 'true' : undefined}
                colors={{ '--fsp-challenge-pos': positionColor(s.position.position) }}
              >
                <span className="fsp-challenge-slot">
                  <PositionPill pos={s.position.position} size="sm" />
                  <Medal n={s.tier + 1} />
                </span>
                <span className="fsp-challenge-entry-name">
                  {p === null ? (
                    <Text tone="secondary">{id === null ? 'Pick one' : 'Picked'}</Text>
                  ) : (
                    <Text weight={isCaptain ? 'strong' : undefined}>{p.player.name}</Text>
                  )}
                </span>
                {isCaptain ? (
                  <Chip icon="crown">Captain</Chip>
                ) : p !== null &&
                  open &&
                  !p.locked &&
                  !(captain !== null && lockedOf(slots, captain)) ? (
                  <Button
                    size="xs"
                    variant="quiet"
                    disabled={busy}
                    label={`Make ${p.player.name} your captain`}
                    onClick={() => onCaptain(p.player.id)}
                  >
                    Captain
                  </Button>
                ) : p?.locked ? (
                  <span role="img" aria-label="Locked: his game has started">
                    <Icon name="locked" size="sm" />
                  </span>
                ) : null}
                {p?.week ? (
                  <span className="fsp-num fsp-challenge-entry-points">
                    {tenths(isCaptain ? p.week.total * view.captain : p.week.total)}
                  </span>
                ) : null}
              </Colored>
            </li>
          );
        })}
      </ol>
      {view.mine !== null && view.mine.place !== null ? (
        <Text size="sm" tone="secondary" block>
          {tenths(view.mine.points)} points
          {numbers
            ? `, the captain’s ${tenths(view.mine.captainPoints)} counted ${view.captain} times`
            : ''}
          .
        </Text>
      ) : null}
      {view.name !== null ? (
        <Line gap={2}>
          <Text size="sm" tone="secondary">
            On everybody’s board as <Text weight="strong">{view.name}</Text>.
          </Text>
          <Button
            size="xs"
            variant="quiet"
            label="Change your name on the board"
            onClick={onRename}
          >
            Change
          </Button>
        </Line>
      ) : null}
    </Panel>
  );
}

/** Whether a player, wherever he sits in the week, has started his game. */
function lockedOf(slots: Slot[], id: string): boolean {
  return slots.some((s) => s.players.some((p) => p.player.id === id && p.locked));
}

/** A drawn position: its tiers, a table each, a row a player with a button to pick him. */
function PositionPanel({
  position,
  phone,
  firstSlot,
  picks,
  captain,
  zone,
  numbers,
  canPick,
  onPick,
}: {
  position: ChallengeWeekViewShape['positions'][number];
  /** A phone's row is the short name and the game's day, so a tier's numbers fit beside them. */
  phone: boolean;
  firstSlot: number;
  picks: (string | null)[];
  captain: string | null;
  zone: string;
  numbers: boolean;
  canPick: boolean;
  onPick: (slot: number, id: string) => void;
}) {
  const cats = position.categories;
  const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: zone });
  return (
    <Panel
      title={plural(position.name)}
      heading={
        <span className="fsp-challenge-pos-heading">
          <PositionPill pos={position.position} />
          {plural(position.name)}
        </span>
      }
      bodyClass="fsp-challenge-body"
    >
      <Colored
        className="fsp-challenge-pos-body"
        colors={{ '--fsp-challenge-pos': positionColor(position.position) }}
      >
        <Stack gap={4}>
          {position.tiers.map((players, t) => {
            const slot = firstSlot + t;
            const mine = picks[slot] ?? null;
            const read = players.some((p) => p.week !== null);
            return (
              <section
                key={t}
                className="fsp-challenge-tier"
                aria-label={`${plural(position.name)}, tier ${t + 1}`}
              >
                <div className="fsp-challenge-tier-head">
                  <Medal n={t + 1} hidden />
                  <Caption>
                    Tier {t + 1}
                    {mine === null ? ' · pick one' : ''}
                  </Caption>
                </div>
                <TableScroll className="fsp-challenge-scroll">
                  <table className="fsp-challenge-table">
                    <thead>
                      <tr>
                        <th scope="col" className="fsp-challenge-pickcol">
                          <span className="fsp-sr">Pick</span>
                        </th>
                        <th scope="col">Player</th>
                        {/* Points a game set the tiers, which a tier already says: a phone leaves them out for the week's numbers. */}
                        {numbers && !phone ? (
                          <th
                            scope="col"
                            className="fsp-num"
                            title="Fantasy points a game, which set the tiers"
                          >
                            Pts/g
                          </th>
                        ) : null}
                        {cats.map((c) => (
                          <th
                            key={c.id}
                            scope="col"
                            className="fsp-num"
                            title={`${c.name}, his average a game`}
                          >
                            {c.short}
                          </th>
                        ))}
                        {read ? (
                          <th scope="col" className="fsp-num" title="His points this week">
                            Week
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {players.map((p) => {
                        const picked = p.player.id === mine;
                        // Locked out of a change: his game has started, or the pick he would replace has.
                        const holder =
                          mine === null ? null : players.find((x) => x.player.id === mine);
                        const stuck = p.locked || (holder?.locked ?? false);
                        return (
                          <tr key={p.player.id} data-picked={picked || undefined}>
                            <td className="fsp-challenge-pickcol">
                              <button
                                type="button"
                                className="fsp-challenge-pick"
                                aria-pressed={picked}
                                aria-label={`${picked ? 'Take out' : 'Pick'} ${p.player.name}`}
                                disabled={!canPick || stuck}
                                onClick={() => onPick(slot, p.player.id)}
                              >
                                {picked ? (
                                  <Icon
                                    name={p.player.id === captain ? 'crown' : 'accept'}
                                    size="sm"
                                  />
                                ) : stuck && p.locked ? (
                                  <Icon name="locked" size="sm" />
                                ) : null}
                              </button>
                            </td>
                            <td>
                              <div className="fsp-challenge-player">
                                <span className="fsp-challenge-player-name">
                                  {phone ? p.player.shortName : p.player.name}{' '}
                                  <StatusBadge player={p.player} />
                                </span>
                                <span className="fsp-challenge-player-game">
                                  <ClubDot code={p.player.proTeam} />
                                  {p.player.proTeam ?? 'No club'}
                                  {p.opponent !== null
                                    ? ` ${p.home ? 'vs' : '@'} ${p.opponent}`
                                    : ''}
                                  {p.kickoff !== null
                                    ? ` · ${p.locked ? 'started' : phone ? day.format(new Date(p.kickoff)) : fmtWhen(p.kickoff, zone)}`
                                    : ''}
                                </span>
                              </div>
                            </td>
                            {numbers && !phone ? (
                              <td className="fsp-num">{p.perGame.toFixed(1)}</td>
                            ) : null}
                            {cats.map((c, i) => {
                              const avg = p.averages[i];
                              return (
                                <td key={c.id} className="fsp-num">
                                  {avg === null || avg === undefined ? '—' : avg.toFixed(1)}
                                </td>
                              );
                            })}
                            {read ? (
                              <td className="fsp-num">
                                {p.week === null ? (
                                  '—'
                                ) : (
                                  <span
                                    title={
                                      numbers
                                        ? cats
                                            .map(
                                              (c, i) =>
                                                `${c.short} ${p.week!.values[i] === null ? '—' : p.week!.values[i]!.toFixed(c.decimals)} = ${tenths(p.week!.points[i]!)}`,
                                            )
                                            .join(', ')
                                        : undefined
                                    }
                                  >
                                    {tenths(p.week.total)}
                                  </span>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </TableScroll>
              </section>
            );
          })}
          <Text size="sm" tone="secondary" block>
            The numbers are each player’s average a game over his last games
            {numbers ? ', and points a game set the tiers' : ''}.
          </Text>
        </Stack>
      </Colored>
    </Panel>
  );
}

/**
 * The boards: my league's members, then everybody's top by their challenge
 * names. Before any pick's line is in, every entry has nothing and a place
 * says nothing: the league's lists who has entered, and everybody's how many.
 */
function Boards({
  board,
  league,
  numbers,
}: {
  board: ChallengeBoardViewShape | null;
  league: ChallengeBoardViewShape | null;
  numbers: boolean;
}) {
  const [all, setAll] = useState(false);
  const top = board === null ? [] : all ? board.rows : board.rows.slice(0, 10);
  const scored = board?.scored ?? false;
  return (
    <>
      {/* Not read, or not mine to read: no board rather than an empty one. */}
      {league === null ? null : (
        <Panel title="Your league" bodyClass="fsp-challenge-body">
          {league.rows.length === 0 ? (
            <Text tone="secondary" block>
              Nobody in your league has entered this week yet.
            </Text>
          ) : (
            <Stack gap={2}>
              {!scored ? (
                <Text size="sm" tone="secondary" block>
                  Entered so far. Places come in the morning after the first game.
                </Text>
              ) : null}
              <ol className="fsp-challenge-board">
                {league.rows.map((r) => (
                  <li key={r.team?.id ?? r.name} data-mine={r.mine || undefined}>
                    {scored ? (
                      <span className="fsp-num fsp-challenge-place">
                        <Medal n={r.place} />
                      </span>
                    ) : null}
                    {r.team !== null ? <TeamMark team={r.team} size={24} /> : null}
                    <span className="fsp-challenge-board-name">{r.name}</span>
                    {scored ? <span className="fsp-num">{tenths(r.points)}</span> : null}
                  </li>
                ))}
              </ol>
              {scored ? (
                <Text size="sm" tone="secondary" block>
                  Places are among everybody on the site.
                </Text>
              ) : null}
            </Stack>
          )}
        </Panel>
      )}
      <Panel title="Everybody" bodyClass="fsp-challenge-body">
        {board === null || board.entries === 0 ? (
          <Text tone="secondary" block>
            No entries yet this week.
          </Text>
        ) : !scored ? (
          <Text tone="secondary" block>
            {board.entries} {board.entries === 1 ? 'entry' : 'entries'} so far. The board fills in
            as the week’s games are read.
          </Text>
        ) : (
          <Stack gap={2}>
            {board.me !== null ? (
              <Text weight="strong" block>
                You’re {ordinal(board.me.place)} of {board.entries}.
              </Text>
            ) : numbers ? (
              <Text size="sm" tone="secondary" block>
                {board.entries} {board.entries === 1 ? 'entry' : 'entries'} this week.
              </Text>
            ) : null}
            <ol className="fsp-challenge-board">
              {top.map((r, i) => (
                <li key={`${r.place}-${r.name}-${i}`} data-mine={r.mine || undefined}>
                  <span className="fsp-num fsp-challenge-place">
                    <Medal n={r.place} />
                  </span>
                  <span className="fsp-challenge-board-name">{r.name}</span>
                  <span className="fsp-num">{tenths(r.points)}</span>
                </li>
              ))}
            </ol>
            {board.rows.length > 10 ? (
              <div>
                <Button size="xs" variant="quiet" onClick={() => setAll(!all)} expanded={all}>
                  {all ? 'Show the top 10' : `Show the top ${board.rows.length}`}
                </Button>
              </div>
            ) : null}
          </Stack>
        )}
      </Panel>
    </>
  );
}

/**
 * THE NAME THE PUBLIC BOARD SHOWS, asked for on the first pick, saying plainly
 * that everybody can see it (CLAUDE.md, say what will happen), and changed
 * from Your entry.
 */
function NameSheet({
  opened,
  picking,
  current,
  onClose,
  onSaved,
}: {
  opened: boolean;
  /** A pick waits on the name: the button says it saves both. */
  picking: boolean;
  /** The name it has now, to start from when changing it. */
  current: string | null;
  onClose: () => void;
  onSaved: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(current ?? '');
  useEffect(() => {
    if (opened) setName(current ?? '');
  }, [opened, current]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    const out = await saveChallengeName(name);
    setSaving(false);
    if (!out.ok) {
      setError(out.message);
      return;
    }
    setError(null);
    await onSaved(out.name);
  };
  return (
    <Dialog opened={opened} onClose={onClose} title="Your name on the board">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Stack gap={3}>
          <Text block>
            Everybody on the site can see this name beside your score on the weekly challenge’s
            board. It’s the only thing about you the board shows, and you can change it later.
          </Text>
          <TextField
            label="Name"
            value={name}
            onChange={(v) => {
              setName(v);
              setError(null);
            }}
            maxLength={24}
            autoFocus
            error={error ?? undefined}
          />
          <Line gap={2}>
            <Button variant="primary" submit loading={saving} disabled={name.trim().length < 2}>
              {picking ? 'Save and pick' : 'Save'}
            </Button>
            <Button variant="quiet" onClick={onClose}>
              Not now
            </Button>
          </Line>
        </Stack>
      </form>
    </Dialog>
  );
}
